import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2, Edit, Calendar, Gauge, Camera, X, ChevronLeft, Wrench, User, Building2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const fmtDate = (d) => (d ? new Date(d.includes('T') ? d : d + 'T00:00:00').toLocaleDateString('pt-BR') : '-');

/**
 * Comprime imagem via canvas: max 1280px no maior lado, qualidade JPEG 75%.
 * Resulta em ~100-200KB por foto. Retorna data URL (base64).
 */
async function comprimirImagem(file, maxSize = 1280, quality = 0.75) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          const ratio = Math.min(maxSize / width, maxSize / height);
          width *= ratio;
          height *= ratio;
        }
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(width);
        canvas.height = Math.round(height);
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Modal de histórico do veículo. Reusável em portal cliente e admin oficina.
 * Props:
 * - open, onOpenChange
 * - veiculo: {id, marca, modelo, placa, ano}
 * - apiClient: instance axios (portalApi ou api)
 * - mode: 'cliente' | 'admin' — define se pode editar registros de outros
 * - basePathListar: ex. '/cliente/veiculos/{id}/historico' ou '/veiculos/{id}/historico'
 * - basePathCrud: ex. '/cliente/historico' ou '/historico'
 */
export default function HistoricoVeiculoModal({ open, onOpenChange, veiculo, apiClient, mode, basePathListar, basePathCrud }) {
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editando, setEditando] = useState(null); // registro em edição
  const [verDetalhe, setVerDetalhe] = useState(null);
  const [form, setForm] = useState({ data: new Date().toISOString().slice(0, 10), km: '', titulo: '', descricao: '', fotos: [] });
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const isCliente = mode === 'cliente';

  const carregar = async () => {
    if (!veiculo?.id) return;
    setLoading(true);
    try {
      const path = basePathListar.replace('{id}', veiculo.id);
      const { data } = await apiClient.get(path);
      // resposta cliente vem como {veiculo, historico}, admin vem como array
      setRegistros(Array.isArray(data) ? data : (data.historico || []));
    } catch (err) {
      toast.error('Erro ao carregar histórico');
    } finally {
      setLoading(false);
    }
  };

  // Recarrega quando muda veiculo (sem useEffect para evitar set-state-in-effect)
  const [lastLoadedVeiculoId, setLastLoadedVeiculoId] = useState(null);
  if (open && veiculo?.id && veiculo.id !== lastLoadedVeiculoId) {
    setLastLoadedVeiculoId(veiculo.id);
    carregar();
    // reset inline
    setForm({ data: new Date().toISOString().slice(0, 10), km: '', titulo: '', descricao: '', fotos: [] });
    setEditando(null);
    setShowForm(false);
  }


  const resetForm = () => {
    setForm({ data: new Date().toISOString().slice(0, 10), km: '', titulo: '', descricao: '', fotos: [] });
    setEditando(null);
    setShowForm(false);
  };

  const abrirNovo = () => {
    resetForm();
    setShowForm(true);
  };

  const abrirEdicao = async (reg) => {
    // buscar registro completo (com fotos)
    try {
      const path = `${basePathCrud}/${reg.id}`;
      const { data } = await apiClient.get(path);
      setEditando(data);
      setForm({
        data: data.data || new Date().toISOString().slice(0, 10),
        km: data.km || '',
        titulo: data.titulo || '',
        descricao: data.descricao || '',
        fotos: data.fotos || [],
      });
      setShowForm(true);
    } catch {
      toast.error('Erro ao carregar registro');
    }
  };

  const abrirDetalhe = async (reg) => {
    try {
      const path = `${basePathCrud}/${reg.id}`;
      const { data } = await apiClient.get(path);
      setVerDetalhe(data);
    } catch {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    toast.info(`Comprimindo ${files.length} foto(s)...`);
    try {
      const compactadas = await Promise.all(
        files.map(async (f) => ({
          _key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          data_url: await comprimirImagem(f),
          nome: f.name,
        }))
      );
      setForm((prev) => ({ ...prev, fotos: [...prev.fotos, ...compactadas] }));
      toast.success(`${compactadas.length} foto(s) adicionada(s)`);
    } catch {
      toast.error('Erro ao processar fotos');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removerFoto = (idx) => {
    setForm((prev) => ({ ...prev, fotos: prev.fotos.filter((_, i) => i !== idx) }));
  };

  const salvar = async () => {
    if (!form.titulo.trim()) {
      toast.error('Título é obrigatório');
      return;
    }
    if (!form.data) {
      toast.error('Data é obrigatória');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        veiculo_id: veiculo.id,
        data: form.data,
        km: form.km ? parseInt(form.km, 10) : null,
        titulo: form.titulo.trim(),
        descricao: form.descricao.trim() || null,
        fotos: form.fotos,
      };
      if (editando) {
        await apiClient.put(`${basePathCrud}/${editando.id}`, payload);
        toast.success('Registro atualizado');
      } else {
        await apiClient.post(basePathCrud, payload);
        toast.success('Registro adicionado');
      }
      await carregar();
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const excluir = async (reg) => {
    if (!window.confirm(`Excluir o registro "${reg.titulo}"?`)) return;
    try {
      await apiClient.delete(`${basePathCrud}/${reg.id}`);
      toast.success('Registro excluído');
      await carregar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir');
    }
  };

  // Determina se o usuário atual pode editar/excluir um registro
  const podeEditar = (reg) => {
    if (!isCliente) return true; // admin pode tudo
    return reg.criado_por === 'cliente'; // cliente só os dele
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-carbon border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="historico-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Wrench className="w-5 h-5 text-blue-400" /> Histórico do Veículo
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {veiculo?.marca} {veiculo?.modelo} — Placa <span className="font-mono text-blue-300">{veiculo?.placa}</span>
            </DialogDescription>
          </DialogHeader>

          {!showForm ? (
            <div className="space-y-3">
              <div className="flex justify-end">
                <Button onClick={abrirNovo} className="bg-blue-500 hover:bg-blue-600 text-white" data-testid="historico-add-btn">
                  <Plus className="w-4 h-4 mr-1" /> Adicionar Registro
                </Button>
              </div>

              {loading ? (
                <div className="text-center py-8 text-slate-400">Carregando...</div>
              ) : registros.length === 0 ? (
                <div className="bg-obsidian/50 border border-white/5 rounded-lg p-8 text-center">
                  <Wrench className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                  <p className="text-slate-400 text-sm">Nenhum registro no histórico ainda</p>
                  <p className="text-xs text-slate-600 mt-1">Adicione o primeiro serviço deste veículo</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {registros.map((reg) => (
                    <div
                      key={reg.id}
                      onClick={() => abrirDetalhe(reg)}
                      className="bg-obsidian border border-white/5 rounded-lg p-3 hover:border-blue-500/30 cursor-pointer transition-colors"
                      data-testid={`historico-item-${reg.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-white font-medium text-sm truncate">{reg.titulo}</h3>
                            {reg.criado_por === 'cliente' ? (
                              <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 flex items-center gap-1 flex-shrink-0">
                                <User className="w-2.5 h-2.5" /> Cliente
                              </span>
                            ) : (
                              <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 flex items-center gap-1 flex-shrink-0">
                                <Building2 className="w-2.5 h-2.5" /> Oficina
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-400">
                            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmtDate(reg.data)}</span>
                            {reg.km && <span className="flex items-center gap-1"><Gauge className="w-3 h-3" /> {reg.km.toLocaleString('pt-BR')} km</span>}
                            {reg.fotos_count > 0 && <span className="flex items-center gap-1 text-blue-300"><Camera className="w-3 h-3" /> {reg.fotos_count} foto(s)</span>}
                          </div>
                          {reg.descricao && <p className="text-xs text-slate-300 mt-1.5 line-clamp-2">{reg.descricao}</p>}
                        </div>
                        {podeEditar(reg) && (
                          <div className="flex gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                            <Button size="sm" variant="ghost" onClick={() => abrirEdicao(reg)} className="h-7 w-7 p-0 text-slate-400 hover:text-blue-400" data-testid={`historico-edit-${reg.id}`}>
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => excluir(reg)} className="h-7 w-7 p-0 text-slate-400 hover:text-red-400" data-testid={`historico-delete-${reg.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3" data-testid="historico-form">
              <Button variant="ghost" onClick={resetForm} className="text-slate-400 hover:text-white -ml-2 mb-2" size="sm">
                <ChevronLeft className="w-4 h-4 mr-1" /> Voltar
              </Button>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-400 uppercase">Data *</Label>
                  <Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })}
                    className="bg-obsidian border-white/10 text-white mt-1" data-testid="historico-form-data" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400 uppercase">Quilometragem</Label>
                  <Input type="number" value={form.km} onChange={(e) => setForm({ ...form, km: e.target.value })}
                    placeholder="Ex: 85000" className="bg-obsidian border-white/10 text-white mt-1" data-testid="historico-form-km" />
                </div>
              </div>

              <div>
                <Label className="text-xs text-slate-400 uppercase">Título do serviço *</Label>
                <Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                  placeholder="Ex: Troca de óleo e filtros" className="bg-obsidian border-white/10 text-white mt-1" data-testid="historico-form-titulo" />
              </div>

              <div>
                <Label className="text-xs text-slate-400 uppercase">Descrição</Label>
                <Textarea value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                  placeholder="Detalhes do serviço, peças trocadas, observações..."
                  rows={3} className="bg-obsidian border-white/10 text-white mt-1 resize-none" data-testid="historico-form-descricao" />
              </div>

              <div>
                <Label className="text-xs text-slate-400 uppercase flex items-center justify-between">
                  <span>Fotos ({form.fotos.length})</span>
                  <Button type="button" size="sm" variant="ghost" onClick={() => fileInputRef.current?.click()}
                    className="h-7 text-blue-400 hover:bg-blue-500/10 normal-case" data-testid="historico-form-add-foto">
                    <Camera className="w-3.5 h-3.5 mr-1" /> Adicionar foto
                  </Button>
                </Label>
                <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileChange} />
                {form.fotos.length === 0 ? (
                  <p className="text-xs text-slate-600 mt-1">Nenhuma foto. Clique acima para adicionar.</p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
                    {form.fotos.map((f, idx) => (
                      <div key={f._key || f.data_url?.slice(-32) || idx} className="relative group">
                        <img src={f.data_url} alt={f.nome || `foto-${idx}`} className="w-full h-20 object-cover rounded border border-white/10" />
                        <button type="button" onClick={() => removerFoto(idx)}
                          className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`historico-foto-remove-${idx}`}>
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {isCliente && (
                <div className="bg-amber-500/5 border border-amber-500/20 rounded p-2 text-[11px] text-amber-300 flex items-start gap-2">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>Este registro ficará marcado como adicionado por você. A oficina poderá visualizar mas não editar.</span>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
                <Button variant="ghost" onClick={resetForm} className="text-slate-400">Cancelar</Button>
                <Button onClick={salvar} disabled={saving} className="bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-50" data-testid="historico-form-salvar">
                  {saving ? 'Salvando...' : (editando ? 'Atualizar' : 'Salvar Registro')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog de detalhes com galeria de fotos */}
      <Dialog open={!!verDetalhe} onOpenChange={() => setVerDetalhe(null)}>
        <DialogContent className="bg-carbon border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="historico-detalhe-modal">
          {verDetalhe && (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg font-bold">{verDetalhe.titulo}</DialogTitle>
                <DialogDescription className="flex flex-wrap items-center gap-3 text-slate-400 text-xs">
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmtDate(verDetalhe.data)}</span>
                  {verDetalhe.km && <span className="flex items-center gap-1"><Gauge className="w-3 h-3" /> {verDetalhe.km.toLocaleString('pt-BR')} km</span>}
                  {verDetalhe.criado_por === 'cliente' ? (
                    <span className="flex items-center gap-1 text-amber-400"><User className="w-3 h-3" /> Adicionado pelo cliente</span>
                  ) : (
                    <span className="flex items-center gap-1 text-blue-400"><Building2 className="w-3 h-3" /> Serviço da oficina</span>
                  )}
                </DialogDescription>
              </DialogHeader>
              {verDetalhe.descricao && (
                <p className="text-sm text-slate-200 whitespace-pre-line">{verDetalhe.descricao}</p>
              )}
              {verDetalhe.fotos && verDetalhe.fotos.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                  {verDetalhe.fotos.map((f, idx) => (
                    <a key={f._key || f.data_url?.slice(-32) || idx} href={f.data_url} target="_blank" rel="noopener noreferrer" className="block">
                      <img src={f.data_url} alt={f.nome || `foto-${idx}`} className="w-full h-32 object-cover rounded border border-white/10 hover:border-blue-500/50 transition-colors" />
                    </a>
                  ))}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
